import { useEffect, useState, useRef } from 'react'
import { Plus, Search, X, UserPlus, UserMinus, Edit, Trash2, Upload, ChevronDown, ArrowUp, ArrowDown } from 'lucide-react'
import { cardsAPI, clientsAPI } from '../api/client'
import { useOrg } from '../contexts/OrgContext'

function CardsPage() {
  const { currentOrg } = useOrg()
  const [cards, setCards] = useState([])
  const [clients, setClients] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [showAddModal, setShowAddModal] = useState(false)
  const [showAssignModal, setShowAssignModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [selectedCard, setSelectedCard] = useState(null)
  const [formData, setFormData] = useState({
    card_id: '',
    notes: ''
  })
  const [editCardData, setEditCardData] = useState({
    status: '',
    notes: ''
  })
  const [selectedClientId, setSelectedClientId] = useState('')
  const [clientSearchTerm, setClientSearchTerm] = useState('')
  const [submitting, setSubmitting] = useState(false)

  // Bulk operations state
  const [showAdvancedMenu, setShowAdvancedMenu] = useState(false)
  const [showImportModal, setShowImportModal] = useState(false)
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false)
  const [sortColumn, setSortColumn] = useState(null)
  const [sortDirection, setSortDirection] = useState('asc')
  const [csvFile, setCsvFile] = useState(null)
  const [importPreview, setImportPreview] = useState(null)
  const [importErrors, setImportErrors] = useState([])
  const [importSuccess, setImportSuccess] = useState([])
  const [importing, setImporting] = useState(false)
  const [editingErrorIndex, setEditingErrorIndex] = useState(null)
  const [selectedForDelete, setSelectedForDelete] = useState([])
  const [deleteConfirmation, setDeleteConfirmation] = useState('')
  const [deleting, setDeleting] = useState(false)
  const [bulkDeleteSearch, setBulkDeleteSearch] = useState('')
  const advancedMenuRef = useRef(null)

  // Pagination state
  const [pageSize, setPageSize] = useState(100)
  const [totalCards, setTotalCards] = useState(0)

  useEffect(() => {
    if (currentOrg?.org_id) {
      loadCards()
      loadClients()
    }
  }, [currentOrg?.org_id])

  async function loadCards() {
    if (!currentOrg?.org_id) return

    try {
      setLoading(true)
      setError('')

      let allCards = []
      let nextToken = null

      // Keep fetching until we have all cards
      do {
        const params = {
          org_id: currentOrg.org_id,
          limit: 100
        }
        if (nextToken) {
          params.nextToken = nextToken
        }

        const response = await cardsAPI.getAll(params)
        const cardsData = response.data?.cards || []
        allCards = [...allCards, ...cardsData]
        nextToken = response.data?.nextToken
      } while (nextToken)

      setCards(allCards)
      setTotalCards(allCards.length)
    } catch (err) {
      setError(err.message || 'Failed to load cards')
      console.error('Cards error:', err)
    } finally {
      setLoading(false)
    }
  }

  async function loadClients() {
    if (!currentOrg?.org_id) return

    try {
      let allClients = []
      let nextToken = null

      // Keep fetching until we have all clients
      do {
        const params = {
          org_id: currentOrg.org_id,
          limit: 100
        }
        if (nextToken) {
          params.nextToken = nextToken
        }

        const response = await clientsAPI.getAll(params)
        const clientsData = response.data?.clients || []
        allClients = [...allClients, ...clientsData]
        nextToken = response.data?.nextToken
      } while (nextToken)

      setClients(allClients)
    } catch (err) {
      console.error('Failed to load clients:', err)
    }
  }

  async function handleAddCard(e) {
    e.preventDefault()
    setError('')
    setSubmitting(true)

    try {
      await cardsAPI.create({
        ...formData,
        org_id: currentOrg.org_id
      })
      setShowAddModal(false)
      setFormData({ card_id: '', notes: '' })
      await loadCards()
    } catch (err) {
      setError(err.message || 'Failed to add card')
    } finally {
      setSubmitting(false)
    }
  }

  async function handleAssignCard(e) {
    e.preventDefault()
    setError('')
    setSubmitting(true)

    try {
      const client = clients.find(c => c.client_id === selectedClientId)
      if (!client) {
        throw new Error('Client not found')
      }

      // Note: Card access (status_rfid) is automatically set based on client_status by the backend
      // Active statuses (current, bolo, probation) = enabled access
      // Inactive statuses (new-client, canceled, blocked, on-hold) = disabled access

      await cardsAPI.assign(selectedCard.card_id, {
        client_id: client.client_id,
        client_name: `${client.first_name} ${client.last_name}`
      })

      setShowAssignModal(false)
      setSelectedCard(null)
      setSelectedClientId('')
      setClientSearchTerm('')
      await loadCards()
      await loadClients() // Refresh clients list so assigned client is removed from available list
    } catch (err) {
      setError(err.message || 'Failed to assign card')
    } finally {
      setSubmitting(false)
    }
  }

  async function handleUnassignCard(card) {
    if (!confirm(`Unassign card ${card.last_5_digits} from ${card.assigned_to_name}?`)) {
      return
    }

    setError('')
    try {
      await cardsAPI.unassign(card.card_id)
      await loadCards()
      await loadClients() // Refresh clients list so unassigned client becomes available
    } catch (err) {
      setError(err.message || 'Failed to unassign card')
    }
  }

  async function handleUpdateStatus(card, newStatus) {
    setError('')
    try {
      await cardsAPI.update(card.card_id, { status: newStatus })
      await loadCards()
    } catch (err) {
      setError(err.message || 'Failed to update card status')
    }
  }

  function openAssignModal(card) {
    setSelectedCard(card)
    setSelectedClientId('')
    setClientSearchTerm('')
    setShowAssignModal(true)
  }

  function openEditModal(card) {
    setSelectedCard(card)
    setEditCardData({
      status: card.status,
      notes: card.notes || ''
    })
    setShowEditModal(true)
  }

  async function handleSaveCardEdit(e) {
    e.preventDefault()
    setError('')
    setSubmitting(true)

    try {
      await cardsAPI.update(selectedCard.card_id, editCardData)
      setShowEditModal(false)
      setSelectedCard(null)
      await loadCards()
    } catch (err) {
      setError(err.message || 'Failed to update card')
    } finally {
      setSubmitting(false)
    }
  }

  async function handleDeleteCard(card) {
    if (!confirm(`Delete card ${card.last_5_digits}${card.assigned_to_name ? ` (assigned to ${card.assigned_to_name})` : ''}? This action cannot be undone.`)) {
      return
    }

    setError('')
    try {
      await cardsAPI.delete(card.card_id)
      await loadCards()
      if (card.assigned_to) {
        await loadClients() // Refresh if card was assigned
      }
    } catch (err) {
      setError(err.message || 'Failed to delete card')
    }
  }

  // CSV Import handlers
  function parseCSV(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = (e) => {
        const text = e.target.result
        const lines = text.split('\n').filter(line => line.trim())

        if (lines.length < 2) {
          reject(new Error('CSV file must contain a header row and at least one data row'))
          return
        }

        const headers = lines[0].split(',').map(h => h.trim().toLowerCase())
        const rows = []

        for (let i = 1; i < lines.length; i++) {
          const values = lines[i].split(',').map(v => v.trim())
          const row = {}
          headers.forEach((header, index) => {
            row[header] = values[index] || ''
          })
          rows.push(row)
        }

        resolve({ headers, rows })
      }
      reader.onerror = () => reject(new Error('Failed to read file'))
      reader.readAsText(file)
    })
  }

  async function handleImportCSV(e) {
    e.preventDefault()
    if (!csvFile) return

    setImporting(true)
    setError('')

    try {
      const { rows } = await parseCSV(csvFile)

      // Fetch all existing cards for duplicate checking
      let allExistingCards = []
      let nextToken = null

      do {
        const params = {
          org_id: currentOrg.org_id,
          limit: 100
        }
        if (nextToken) {
          params.nextToken = nextToken
        }

        const response = await cardsAPI.getAll(params)
        const cardsData = response.data?.cards || []
        allExistingCards = [...allExistingCards, ...cardsData]
        nextToken = response.data?.nextToken
      } while (nextToken)

      const validCards = []
      const errors = []

      for (let i = 0; i < rows.length; i++) {
        const row = rows[i]
        const rowErrors = []

        if (!row.card_id) rowErrors.push('Missing card_id')

        // Check for duplicates in existing cards
        const isDuplicate = allExistingCards.some(c => c.card_id === row.card_id)
        if (isDuplicate) rowErrors.push('Card already exists')

        if (rowErrors.length > 0) {
          errors.push({ row: i + 2, data: row, errors: rowErrors })
        } else {
          validCards.push({
            card_id: row.card_id,
            notes: row.notes || '',
            org_id: currentOrg.org_id
          })
        }
      }

      // Import valid cards
      let successCount = 0
      for (const cardData of validCards) {
        try {
          await cardsAPI.create(cardData)
          successCount++
        } catch (err) {
          errors.push({
            row: -1,
            data: cardData,
            errors: [err.message || 'Failed to create card']
          })
        }
      }

      setImportSuccess(Array(successCount).fill({}))
      setImportErrors(errors)

      if (errors.length === 0) {
        setTimeout(() => {
          setShowImportModal(false)
          resetImport()
        }, 2000)
      }

      await loadCards()
    } catch (err) {
      setError(err.message || 'Failed to import cards')
    } finally {
      setImporting(false)
    }
  }

  function resetImport() {
    setCsvFile(null)
    setImportPreview(null)
    setImportErrors([])
    setImportSuccess([])
    setEditingErrorIndex(null)
  }

  function downloadSampleCSV() {
    const csvContent = 'card_id,notes\n1234567890,Optional notes\n9876543210,Another card'
    const blob = new Blob([csvContent], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'sample_cards_import.csv'
    a.click()
    URL.revokeObjectURL(url)
  }

  function updateErrorRow(index, field, value) {
    const updatedErrors = [...importErrors]
    updatedErrors[index].data[field] = value
    setImportErrors(updatedErrors)
  }

  function validateErrorRow(rowData) {
    const errors = []
    if (!rowData.card_id) errors.push('Missing card_id')

    const isDuplicate = cards.some(c => c.card_id === rowData.card_id)
    if (isDuplicate) errors.push('Duplicate: Card already exists')

    return errors
  }

  async function retryImportErrorRow(index) {
    const errorItem = importErrors[index]
    const validationErrors = validateErrorRow(errorItem.data)

    if (validationErrors.length > 0) {
      const updatedErrors = [...importErrors]
      updatedErrors[index].errors = validationErrors
      setImportErrors(updatedErrors)
      return
    }

    setImporting(true)
    try {
      const cardData = {
        card_id: errorItem.data.card_id,
        notes: errorItem.data.notes || '',
        org_id: currentOrg.org_id
      }

      await cardsAPI.create(cardData)

      const updatedErrors = importErrors.filter((_, i) => i !== index)
      setImportErrors(updatedErrors)
      setImportSuccess([...importSuccess, errorItem])
      await loadCards()
    } catch (err) {
      const updatedErrors = [...importErrors]
      updatedErrors[index].errors = [err.message || 'Failed to import']
      setImportErrors(updatedErrors)
    } finally {
      setImporting(false)
      setEditingErrorIndex(null)
    }
  }

  // Bulk Delete handlers
  function toggleSelectForDelete(cardId) {
    setSelectedForDelete(prev =>
      prev.includes(cardId)
        ? prev.filter(id => id !== cardId)
        : [...prev, cardId]
    )
  }

  function getCardsWithAssignments() {
    return selectedForDelete.filter(cardId => {
      const card = cards.find(c => c.card_id === cardId)
      return card?.assigned_to
    })
  }

  async function handleBulkDelete() {
    if (deleteConfirmation.toLowerCase() !== 'delete') {
      setError('Please type "delete" to confirm')
      return
    }

    const cardsWithAssignments = getCardsWithAssignments()
    if (cardsWithAssignments.length > 0) {
      setError(`${cardsWithAssignments.length} card(s) are currently assigned. Unassign them first before deletion.`)
      return
    }

    setDeleting(true)
    setError('')

    try {
      for (const cardId of selectedForDelete) {
        await cardsAPI.delete(cardId)
      }

      await loadCards()
      setShowBulkDeleteModal(false)
      setSelectedForDelete([])
      setDeleteConfirmation('')
    } catch (err) {
      setError(err.message || 'Failed to delete cards')
    } finally {
      setDeleting(false)
    }
  }

  function closeBulkDeleteModal() {
    setShowBulkDeleteModal(false)
    setSelectedForDelete([])
    setDeleteConfirmation('')
    setBulkDeleteSearch('')
    setError('')
  }

  // Close advanced menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (advancedMenuRef.current && !advancedMenuRef.current.contains(event.target)) {
        setShowAdvancedMenu(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Handle column sorting
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortColumn(column)
      setSortDirection('asc')
    }
  }

  // Filter cards based on status and search
  let allFilteredCards = cards.filter(card => {
    // Status filter
    if (statusFilter !== 'all' && card.status !== statusFilter) {
      return false
    }

    // Search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase()
      return (
        card.card_id?.includes(searchTerm) ||
        card.last_5_digits?.includes(searchTerm) ||
        card.assigned_to_name?.toLowerCase().includes(searchLower) ||
        card.assigned_to_client_id?.toLowerCase().includes(searchLower)
      )
    }

    return true
  })

  // Sort filtered cards if sort column is set
  if (sortColumn) {
    allFilteredCards = [...allFilteredCards].sort((a, b) => {
      let aVal, bVal

      switch (sortColumn) {
        case 'card_id':
          aVal = a.card_id || ''
          bVal = b.card_id || ''
          break
        case 'last_5_digits':
          aVal = a.last_5_digits || ''
          bVal = b.last_5_digits || ''
          break
        case 'assigned_to':
          aVal = a.assigned_to_name || ''
          bVal = b.assigned_to_name || ''
          break
        case 'status':
          aVal = a.status || ''
          bVal = b.status || ''
          break
        default:
          return 0
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1
      return 0
    })
  }

  // Apply client-side pagination
  const filteredCards = allFilteredCards.slice(0, pageSize)

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    )
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>Cards <span style={{ fontSize: '1.25rem', fontWeight: '500', color: 'var(--text-light)' }}>({cards.length})</span></h1>
          <p>Manage RFID cards and assignments</p>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', position: 'relative' }} ref={advancedMenuRef}>
          <button
            className="btn btn-secondary"
            onClick={() => setShowAdvancedMenu(!showAdvancedMenu)}
            style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
          >
            Advanced
            <ChevronDown size={16} />
          </button>

          {showAdvancedMenu && (
            <div style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              marginTop: '0.25rem',
              backgroundColor: 'var(--surface)',
              border: '1px solid var(--border)',
              borderRadius: '0.375rem',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
              minWidth: '180px',
              zIndex: 1000
            }}>
              <button
                onClick={() => {
                  setShowImportModal(true)
                  setShowAdvancedMenu(false)
                }}
                style={{
                  width: '100%',
                  padding: '0.75rem 1rem',
                  border: 'none',
                  background: 'none',
                  textAlign: 'left',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  color: 'var(--text)',
                  fontSize: '0.875rem'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = 'var(--background)'}
                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
              >
                <Upload size={16} />
                Import CSV
              </button>
              <button
                onClick={() => {
                  setShowBulkDeleteModal(true)
                  setShowAdvancedMenu(false)
                }}
                style={{
                  width: '100%',
                  padding: '0.75rem 1rem',
                  border: 'none',
                  background: 'none',
                  textAlign: 'left',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  color: 'var(--text)',
                  fontSize: '0.875rem',
                  borderTop: '1px solid var(--border)'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = 'var(--background)'}
                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
              >
                <Trash2 size={16} />
                Bulk Delete
              </button>
            </div>
          )}

          <button className="btn btn-primary" onClick={() => {
            setError('')
            setShowAddModal(true)
          }}>
            <Plus size={18} />
            Add Card
          </button>
        </div>
      </div>

      {error && !showAddModal && !showAssignModal && !showEditModal && <div className="alert alert-danger">{error}</div>}

      <div className="card">
        {/* Search and Filter Controls */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '1rem',
          marginBottom: '1.5rem',
          flexWrap: 'wrap'
        }}>
          {/* Search bar with clear button */}
          <div style={{ position: 'relative', flex: '1 1 300px', minWidth: '250px' }}>
            <Search size={18} style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-light)' }} />
            <input
              type="text"
              placeholder="Search by card ID, last 5 digits, or client..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ paddingLeft: '2.5rem', paddingRight: searchTerm ? '2.5rem' : '0.75rem', width: '100%' }}
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                style={{
                  position: 'absolute',
                  right: '0.5rem',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: 'var(--text-light)',
                  padding: '0.25rem',
                  display: 'flex',
                  alignItems: 'center'
                }}
                title="Clear search"
              >
                <X size={16} />
              </button>
            )}
          </div>

          {/* Status filter */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <label htmlFor="statusFilter" style={{ fontSize: '0.875rem', fontWeight: '500', whiteSpace: 'nowrap' }}>
              Status:
            </label>
            <select
              id="statusFilter"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              style={{
                padding: '0.375rem 0.75rem',
                fontSize: '0.875rem',
                minWidth: '120px'
              }}
            >
              <option value="all">All Status</option>
              <option value="unassigned">Unassigned</option>
              <option value="assigned">Assigned</option>
              <option value="lost">Lost</option>
              <option value="damaged">Damaged</option>
            </select>
          </div>

          {/* Show per page */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginLeft: 'auto' }}>
            <label htmlFor="pageSize" style={{ fontSize: '0.875rem', fontWeight: '500', whiteSpace: 'nowrap' }}>
              Show per page:
            </label>
            <select
              id="pageSize"
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
              style={{
                padding: '0.375rem 0.75rem',
                fontSize: '0.875rem',
                minWidth: '80px'
              }}
            >
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={200}>200</option>
              <option value={500}>500</option>
              <option value={1000}>1000</option>
            </select>
          </div>
        </div>

        {/* Results count */}
        <div style={{
          fontSize: '0.875rem',
          color: 'var(--text-light)',
          marginBottom: '1rem',
          paddingLeft: '0.5rem'
        }}>
          Showing {Math.min(filteredCards.length, allFilteredCards.length)} of {allFilteredCards.length} cards
          {allFilteredCards.length !== totalCards && (
            <span style={{ marginLeft: '0.25rem' }}>
              (filtered from {totalCards} total)
            </span>
          )}
        </div>

        {filteredCards.length === 0 ? (
          <p className="no-data">No cards found</p>
        ) : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th onClick={() => handleSort('card_id')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                      Card ID
                      {sortColumn === 'card_id' && (
                        sortDirection === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />
                      )}
                    </div>
                  </th>
                  <th onClick={() => handleSort('last_5_digits')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                      Last 5 Digits
                      {sortColumn === 'last_5_digits' && (
                        sortDirection === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />
                      )}
                    </div>
                  </th>
                  <th onClick={() => handleSort('status')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                      Status
                      {sortColumn === 'status' && (
                        sortDirection === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />
                      )}
                    </div>
                  </th>
                  <th onClick={() => handleSort('assigned_to')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                      Assigned To
                      {sortColumn === 'assigned_to' && (
                        sortDirection === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />
                      )}
                    </div>
                  </th>
                  <th>Assigned Date</th>
                  <th>Notes</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredCards.map((card) => (
                  <tr key={card.card_id}>
                    <td style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>{card.card_id}</td>
                    <td style={{ fontFamily: 'monospace', fontWeight: '600' }}>{card.last_5_digits}</td>
                    <td>
                      <span className={`badge ${
                        card.status === 'assigned' ? 'badge-success' :
                        card.status === 'unassigned' ? 'badge-info' :
                        card.status === 'lost' ? 'badge-danger' :
                        'badge-warning'
                      }`}>
                        {card.status}
                      </span>
                    </td>
                    <td>{card.assigned_to_name || '-'}</td>
                    <td>{card.assigned_at ? new Date(card.assigned_at).toLocaleDateString() : '-'}</td>
                    <td style={{ textAlign: 'center' }}>
                      {(() => {
                        if (!card.assigned_to) return '-'
                        const client = clients.find(c => c.client_id === card.assigned_to)
                        const hasNotes = client?.internal_note || client?.external_note
                        return hasNotes ? '📝' : '-'
                      })()}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                        {card.status === 'unassigned' && (
                          <button
                            className="btn btn-primary"
                            style={{ fontSize: '0.75rem', padding: '0.375rem 0.75rem' }}
                            onClick={() => openAssignModal(card)}
                          >
                            <UserPlus size={14} />
                            Assign
                          </button>
                        )}
                        {card.status === 'assigned' && (
                          <button
                            className="btn btn-secondary"
                            style={{ fontSize: '0.75rem', padding: '0.375rem 0.75rem' }}
                            onClick={() => handleUnassignCard(card)}
                          >
                            <UserMinus size={14} />
                            Unassign
                          </button>
                        )}
                        <button
                          className="btn btn-secondary"
                          style={{ fontSize: '0.75rem', padding: '0.375rem 0.75rem' }}
                          onClick={() => openEditModal(card)}
                        >
                          <Edit size={14} />
                          Edit
                        </button>
                        <button
                          className="btn btn-secondary"
                          style={{ fontSize: '0.75rem', padding: '0.375rem 0.75rem' }}
                          onClick={() => handleDeleteCard(card)}
                        >
                          <Trash2 size={14} />
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add Card Modal */}
      {showAddModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'var(--surface)',
            borderRadius: '8px',
            padding: '2rem',
            width: '90%',
            maxWidth: '500px',
            color: 'var(--text)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h2 style={{ margin: 0 }}>Add New Card</h2>
              <button
                onClick={() => {
                  setShowAddModal(false)
                  setError('')
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '0.25rem'
                }}
              >
                <X size={24} />
              </button>
            </div>

            {error && (
              <div className="alert alert-danger" style={{ marginBottom: '1.5rem' }}>
                {error}
              </div>
            )}

            <form onSubmit={handleAddCard}>
              <div className="form-group">
                <label htmlFor="card_id">Card ID (Full Number) *</label>
                <input
                  id="card_id"
                  type="text"
                  value={formData.card_id}
                  onChange={(e) => setFormData({ ...formData, card_id: e.target.value })}
                  placeholder="1234567890"
                  required
                  style={{ fontFamily: 'monospace' }}
                />
                <small>Enter the full RFID card number</small>
              </div>

              <div className="form-group">
                <label htmlFor="notes">Notes</label>
                <textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Optional notes about this card..."
                  rows="3"
                  style={{ width: '100%', padding: '0.5rem 0.75rem', borderRadius: '0.375rem', border: '1px solid var(--border)' }}
                />
              </div>

              <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.5rem' }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowAddModal(false)
                    setError('')
                  }}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={submitting}
                  style={{ marginLeft: 'auto' }}
                >
                  {submitting ? 'Adding...' : 'Add Card'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Assign Card Modal */}
      {showAssignModal && selectedCard && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'var(--surface)',
            borderRadius: '8px',
            padding: '2rem',
            width: '90%',
            maxWidth: '500px',
            color: 'var(--text)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h2 style={{ margin: 0 }}>Assign Card</h2>
              <button
                onClick={() => {
                  setShowAssignModal(false)
                  setClientSearchTerm('')
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '0.25rem'
                }}
              >
                <X size={24} />
              </button>
            </div>

            <div style={{ marginBottom: '1.5rem', padding: '1rem', backgroundColor: 'var(--background)', borderRadius: '0.5rem' }}>
              <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--text-light)' }}>Card ID</p>
              <p style={{ margin: '0.25rem 0 0', fontFamily: 'monospace', fontWeight: '600' }}>{selectedCard.card_id}</p>
              <p style={{ margin: '0.5rem 0 0', fontSize: '0.875rem', color: 'var(--text-light)' }}>Last 5 Digits</p>
              <p style={{ margin: '0.25rem 0 0', fontFamily: 'monospace', fontWeight: '600', fontSize: '1.25rem' }}>{selectedCard.last_5_digits}</p>
            </div>

            <form onSubmit={handleAssignCard}>
              <div className="form-group">
                <label htmlFor="client_search">Search Client</label>
                <div style={{ position: 'relative', marginBottom: '0.5rem' }}>
                  <Search size={18} style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-light)' }} />
                  <input
                    id="client_search"
                    type="text"
                    placeholder="Search by name or ID..."
                    value={clientSearchTerm}
                    onChange={(e) => setClientSearchTerm(e.target.value)}
                    style={{ paddingLeft: '2.5rem', paddingRight: clientSearchTerm ? '2.5rem' : '0.75rem' }}
                  />
                  {clientSearchTerm && (
                    <button
                      type="button"
                      onClick={() => setClientSearchTerm('')}
                      style={{
                        position: 'absolute',
                        right: '0.5rem',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        color: 'var(--text-light)',
                        padding: '0.25rem',
                        display: 'flex',
                        alignItems: 'center'
                      }}
                    >
                      <X size={16} />
                    </button>
                  )}
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="client_select">Select Client *</label>
                <select
                  id="client_select"
                  value={selectedClientId}
                  onChange={(e) => setSelectedClientId(e.target.value)}
                  required
                  size="8"
                  style={{
                    width: '100%',
                    minHeight: '200px',
                    padding: '0.5rem'
                  }}
                >
                  <option value="">-- Select a client --</option>
                  {clients
                    .filter(c => !c.rfid_card_number)
                    .filter(c => {
                      if (!clientSearchTerm) return true
                      const searchLower = clientSearchTerm.toLowerCase()
                      return (
                        c.first_name.toLowerCase().includes(searchLower) ||
                        c.last_name.toLowerCase().includes(searchLower) ||
                        `${c.first_name} ${c.last_name}`.toLowerCase().includes(searchLower) ||
                        c.client_id.includes(searchLower)
                      )
                    })
                    .map(client => (
                      <option key={client.client_id} value={client.client_id}>
                        {client.first_name} {client.last_name} ({client.client_id}) - RFID: {client.status_rfid || 'inactive'}, Status: {client.client_status || 'active'}
                      </option>
                    ))}
                </select>
                <small>Only unassigned clients shown. Warning: Cards may not work if RFID status is inactive or client status is not active.</small>
              </div>

              <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.5rem' }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowAssignModal(false)
                    setClientSearchTerm('')
                  }}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={submitting}
                  style={{ marginLeft: 'auto' }}
                >
                  {submitting ? 'Assigning...' : 'Assign Card'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Card Modal */}
      {showEditModal && selectedCard && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'var(--surface)',
            borderRadius: '8px',
            padding: '2rem',
            width: '90%',
            maxWidth: '500px',
            color: 'var(--text)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h2 style={{ margin: 0 }}>Edit Card</h2>
              <button
                onClick={() => setShowEditModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '0.25rem'
                }}
              >
                <X size={24} />
              </button>
            </div>

            <div style={{ marginBottom: '1.5rem', padding: '1rem', backgroundColor: 'var(--background)', borderRadius: '0.5rem' }}>
              <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--text-light)' }}>Card ID</p>
              <p style={{ margin: '0.25rem 0 0', fontFamily: 'monospace', fontWeight: '600' }}>{selectedCard.card_id}</p>
              <p style={{ margin: '0.5rem 0 0', fontSize: '0.875rem', color: 'var(--text-light)' }}>Last 5 Digits</p>
              <p style={{ margin: '0.25rem 0 0', fontFamily: 'monospace', fontWeight: '600', fontSize: '1.25rem' }}>{selectedCard.last_5_digits}</p>
            </div>

            <form onSubmit={handleSaveCardEdit}>
              <div className="form-group">
                <label htmlFor="card_status">Card Status *</label>
                <select
                  id="card_status"
                  value={editCardData.status}
                  onChange={(e) => setEditCardData({ ...editCardData, status: e.target.value })}
                  disabled={editCardData.status === 'assigned'}
                  required
                >
                  {editCardData.status === 'assigned' && <option value="assigned">Assigned</option>}
                  <option value="unassigned">Unassigned</option>
                  <option value="lost">Lost</option>
                  <option value="damaged">Damaged</option>
                </select>
                <small>
                  {editCardData.status === 'assigned'
                    ? 'Card is currently assigned. Use the Unassign button to unassign before changing status.'
                    : 'Mark card as lost or damaged. Found cards should be marked as unassigned.'}
                </small>
              </div>

              <div className="form-group">
                <label htmlFor="card_notes">Notes</label>
                <textarea
                  id="card_notes"
                  value={editCardData.notes}
                  onChange={(e) => setEditCardData({ ...editCardData, notes: e.target.value })}
                  placeholder="Optional notes about this card..."
                  rows="3"
                  style={{ width: '100%', padding: '0.5rem 0.75rem', borderRadius: '0.375rem', border: '1px solid var(--border)' }}
                />
              </div>

              <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.5rem' }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowEditModal(false)}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={submitting}
                  style={{ marginLeft: 'auto' }}
                >
                  {submitting ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Import CSV Modal */}
      {showImportModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'var(--surface)',
            borderRadius: '8px',
            padding: '2rem',
            width: '90%',
            maxWidth: '800px',
            maxHeight: '90vh',
            overflow: 'auto',
            color: 'var(--text)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h2 style={{ margin: 0 }}>Import Cards from CSV</h2>
              <button
                onClick={() => {
                  setShowImportModal(false)
                  resetImport()
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '1.5rem',
                  cursor: 'pointer',
                  color: 'var(--text)',
                  padding: '0.25rem'
                }}
              >
                <X size={24} />
              </button>
            </div>

            {!csvFile && !importPreview && importErrors.length === 0 && (
              <div style={{ marginBottom: '1.5rem' }}>
                <div style={{
                  backgroundColor: 'var(--background)',
                  padding: '1rem',
                  borderRadius: '4px',
                  marginBottom: '1rem',
                  border: '1px solid var(--border)'
                }}>
                  <p style={{ fontSize: '0.875rem', color: 'var(--text)', marginBottom: '0.75rem', fontWeight: '500' }}>
                    CSV Format Requirements:
                  </p>
                  <ul style={{ fontSize: '0.875rem', color: 'var(--text-light)', margin: '0 0 0.75rem 1.25rem', paddingLeft: 0 }}>
                    <li><strong>Required column:</strong> card_id</li>
                    <li><strong>Optional column:</strong> notes</li>
                    <li><strong>Card ID:</strong> Full RFID card number</li>
                    <li>Duplicates will be rejected automatically</li>
                  </ul>
                  <button
                    onClick={downloadSampleCSV}
                    className="btn btn-secondary"
                    style={{ fontSize: '0.875rem', padding: '0.375rem 0.75rem' }}
                  >
                    Download Sample CSV
                  </button>
                </div>

                <input
                  type="file"
                  accept=".csv"
                  onChange={(e) => {
                    const file = e.target.files[0]
                    if (!file) return

                    if (!file.name.endsWith('.csv')) {
                      setError('Please select a CSV file')
                      return
                    }

                    setCsvFile(file)
                    parseCSV(file)
                      .then(({ rows }) => {
                        setImportPreview(rows)
                      })
                      .catch(err => {
                        setError(err.message)
                      })
                  }}
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    border: '1px solid var(--border)',
                    borderRadius: '4px',
                    backgroundColor: 'var(--surface)',
                    color: 'var(--text)'
                  }}
                />
              </div>
            )}

            {importPreview && importPreview.length > 0 && (
              <div style={{ marginBottom: '1.5rem' }}>
                <h3 style={{ fontSize: '1rem', marginBottom: '0.5rem', color: 'var(--success)' }}>
                  Preview - {importPreview.length} card(s) ready to import
                </h3>
                <div style={{ maxHeight: '200px', overflow: 'auto', border: '1px solid var(--border)', borderRadius: '4px', padding: '0.5rem' }}>
                  {importPreview.map((row, idx) => (
                    <div key={idx} style={{ fontSize: '0.875rem', padding: '0.25rem 0', borderBottom: idx < importPreview.length - 1 ? '1px solid var(--border)' : 'none' }}>
                      {row.card_id} {row.notes ? `- ${row.notes}` : ''}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {importErrors && importErrors.length > 0 && (
              <div style={{ marginBottom: '1.5rem' }}>
                <h3 style={{ fontSize: '1rem', marginBottom: '0.5rem', color: 'var(--danger)' }}>
                  Errors ({importErrors.length}) - Click to edit and retry
                </h3>
                <div style={{ maxHeight: '400px', overflow: 'auto', border: '1px solid var(--danger)', borderRadius: '4px', padding: '0.5rem' }}>
                  {importErrors.map((item, idx) => (
                    <div key={idx} style={{ fontSize: '0.875rem', padding: '0.75rem', marginBottom: '0.5rem', backgroundColor: 'var(--background)', borderRadius: '4px', border: '1px solid var(--border)' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                        <strong>Row {item.row}</strong>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          {editingErrorIndex === idx ? (
                            <>
                              <button
                                onClick={() => retryImportErrorRow(idx)}
                                className="btn btn-primary"
                                style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                                disabled={importing}
                              >
                                {importing ? 'Saving...' : 'Retry Import'}
                              </button>
                              <button
                                onClick={() => setEditingErrorIndex(null)}
                                className="btn btn-secondary"
                                style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                              >
                                Cancel
                              </button>
                            </>
                          ) : (
                            <button
                              onClick={() => setEditingErrorIndex(idx)}
                              className="btn btn-secondary"
                              style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                            >
                              Edit
                            </button>
                          )}
                        </div>
                      </div>

                      {editingErrorIndex === idx ? (
                        <div style={{ display: 'grid', gap: '0.5rem', marginBottom: '0.5rem' }}>
                          <div>
                            <label style={{ fontSize: '0.75rem', fontWeight: '600', display: 'block', marginBottom: '0.25rem' }}>Card ID</label>
                            <input
                              type="text"
                              value={item.data.card_id || ''}
                              onChange={(e) => updateErrorRow(idx, 'card_id', e.target.value)}
                              style={{ width: '100%', padding: '0.25rem 0.5rem', fontSize: '0.875rem', borderRadius: '4px', border: '1px solid var(--border)', fontFamily: 'monospace' }}
                            />
                          </div>
                          <div>
                            <label style={{ fontSize: '0.75rem', fontWeight: '600', display: 'block', marginBottom: '0.25rem' }}>Notes (optional)</label>
                            <input
                              type="text"
                              value={item.data.notes || ''}
                              onChange={(e) => updateErrorRow(idx, 'notes', e.target.value)}
                              style={{ width: '100%', padding: '0.25rem 0.5rem', fontSize: '0.875rem', borderRadius: '4px', border: '1px solid var(--border)' }}
                            />
                          </div>
                        </div>
                      ) : (
                        <div style={{ marginBottom: '0.5rem', fontSize: '0.875rem' }}>
                          Card ID: {item.data?.card_id || '(missing)'} {item.data?.notes && `| Notes: ${item.data.notes}`}
                        </div>
                      )}

                      <ul style={{ margin: '0 0 0 1.5rem', padding: 0, color: 'var(--danger)' }}>
                        {item.errors.map((err, errIdx) => (
                          <li key={errIdx} style={{ fontSize: '0.75rem' }}>{err}</li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {importSuccess && importSuccess.length > 0 && (
              <div style={{ marginBottom: '1.5rem', padding: '1rem', backgroundColor: '#d1fae5', color: '#047857', borderRadius: '4px', fontSize: '1rem', fontWeight: '500' }}>
                Successfully imported {importSuccess.length} card(s)
              </div>
            )}

            {error && (
              <div className="alert alert-danger" style={{ marginBottom: '1rem' }}>
                {error}
              </div>
            )}

            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={() => {
                setShowImportModal(false)
                resetImport()
              }}>
                {importSuccess && importSuccess.length > 0 ? 'Done' : 'Cancel'}
              </button>
              {importPreview && importPreview.length > 0 && importErrors.length === 0 && (
                <button
                  className="btn btn-primary"
                  onClick={handleImportCSV}
                  disabled={importing}
                >
                  {importing ? 'Importing...' : `Import ${importPreview.length} Card(s)`}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Bulk Delete Modal */}
      {showBulkDeleteModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'var(--surface)',
            borderRadius: '8px',
            padding: '2rem',
            width: '90%',
            maxWidth: '800px',
            maxHeight: '90vh',
            overflow: 'auto',
            color: 'var(--text)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h2 style={{ margin: 0 }}>Bulk Delete Cards</h2>
              <button
                onClick={closeBulkDeleteModal}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '0.25rem'
                }}
              >
                <X size={24} />
              </button>
            </div>

            {error && (
              <div className="alert alert-danger" style={{ marginBottom: '1rem' }}>
                {error}
              </div>
            )}

            <div style={{ marginBottom: '1.5rem' }}>
              <div style={{ marginBottom: '1rem' }}>
                <input
                  type="text"
                  placeholder="Search cards by ID or last 5 digits..."
                  value={bulkDeleteSearch}
                  onChange={(e) => setBulkDeleteSearch(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    border: '1px solid var(--border)',
                    borderRadius: '4px',
                    backgroundColor: 'var(--surface)',
                    color: 'var(--text)',
                    fontSize: '0.875rem'
                  }}
                />
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    ref={(el) => {
                      if (el) {
                        const filteredCards = cards.filter(c =>
                          c.card_id?.includes(bulkDeleteSearch) ||
                          c.last_5_digits?.includes(bulkDeleteSearch)
                        )
                        const filteredIds = filteredCards.map(c => c.card_id)
                        const selectedFilteredCount = filteredIds.filter(id => selectedForDelete.includes(id)).length
                        const allFilteredSelected = selectedFilteredCount === filteredCards.length && filteredCards.length > 0
                        const someFilteredSelected = selectedFilteredCount > 0 && selectedFilteredCount < filteredCards.length

                        el.checked = allFilteredSelected
                        el.indeterminate = someFilteredSelected
                      }
                    }}
                    onChange={() => {
                      const filteredCards = cards.filter(c =>
                        c.card_id?.includes(bulkDeleteSearch) ||
                        c.last_5_digits?.includes(bulkDeleteSearch)
                      )
                      const filteredIds = filteredCards.map(c => c.card_id)
                      const allFilteredSelected = filteredIds.every(id => selectedForDelete.includes(id))

                      if (allFilteredSelected) {
                        // Deselect all filtered
                        setSelectedForDelete(selectedForDelete.filter(id => !filteredIds.includes(id)))
                      } else {
                        // Select all filtered (add to existing selection)
                        setSelectedForDelete([...new Set([...selectedForDelete, ...filteredIds])])
                      }
                    }}
                    style={{ width: '1rem', height: '1rem' }}
                  />
                  <span style={{ fontWeight: '600' }}>
                    {bulkDeleteSearch ? 'Select All in Search Results' : 'Select All'}
                    {' '}({cards.filter(c =>
                      c.card_id?.includes(bulkDeleteSearch) ||
                      c.last_5_digits?.includes(bulkDeleteSearch)
                    ).length})
                  </span>
                </label>
                <span style={{ color: 'var(--text-light)', fontSize: '0.875rem' }}>
                  {selectedForDelete.length} card(s) selected total
                </span>
              </div>

              <div style={{
                maxHeight: '300px',
                overflow: 'auto',
                border: '1px solid var(--border)',
                borderRadius: '4px',
                padding: '0.5rem'
              }}>
                {cards
                  .filter(card =>
                    card.card_id?.includes(bulkDeleteSearch) ||
                    card.last_5_digits?.includes(bulkDeleteSearch)
                  )
                  .map(card => (
                  <div
                    key={card.card_id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.75rem',
                      padding: '0.5rem',
                      marginBottom: '0.25rem',
                      backgroundColor: selectedForDelete.includes(card.card_id) ? 'var(--background)' : 'transparent',
                      borderRadius: '4px'
                    }}
                  >
                    <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={selectedForDelete.includes(card.card_id)}
                        onChange={() => toggleSelectForDelete(card.card_id)}
                        style={{ width: '1rem', height: '1rem' }}
                      />
                    </label>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                        <span style={{ fontFamily: 'monospace', fontWeight: '600', fontSize: '0.875rem' }}>
                          {card.last_5_digits}
                        </span>
                        <span className={`badge ${
                          card.status === 'assigned' ? 'badge-success' :
                          card.status === 'unassigned' ? 'badge-info' :
                          card.status === 'lost' ? 'badge-danger' :
                          'badge-warning'
                        }`} style={{ fontSize: '0.625rem' }}>
                          {card.status}
                        </span>
                      </div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-light)' }}>
                        {card.assigned_to_name ? `Assigned to: ${card.assigned_to_name}` : 'Unassigned'}
                      </div>
                    </div>
                    {card.assigned_to && (
                      <span className="badge badge-warning" style={{ fontSize: '0.75rem' }}>
                        Assigned
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {getCardsWithAssignments().length > 0 && (
              <div style={{
                marginBottom: '1.5rem',
                padding: '1rem',
                backgroundColor: '#fef3c7',
                border: '2px solid #f59e0b',
                borderRadius: '4px',
                color: '#92400e'
              }}>
                <p style={{ margin: 0, fontWeight: '600', fontSize: '0.9375rem' }}>
                  Warning: {getCardsWithAssignments().length} card(s) are currently assigned to clients.
                </p>
                <p style={{ margin: '0.5rem 0 0', fontSize: '0.875rem' }}>
                  These cards must be unassigned before they can be deleted. Please unassign them first.
                </p>
              </div>
            )}

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                Type "delete" to confirm deletion of {selectedForDelete.length} card(s):
              </label>
              <input
                type="text"
                value={deleteConfirmation}
                onChange={(e) => setDeleteConfirmation(e.target.value)}
                placeholder="Type delete to confirm"
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: '1px solid var(--border)',
                  borderRadius: '4px',
                  backgroundColor: 'var(--surface)',
                  color: 'var(--text)'
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button
                className="btn btn-danger"
                onClick={handleBulkDelete}
                disabled={selectedForDelete.length === 0 || deleting}
              >
                {deleting ? 'Deleting...' : `Delete ${selectedForDelete.length} Card(s)`}
              </button>
              <button className="btn btn-secondary" onClick={closeBulkDeleteModal}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default CardsPage
